import { useState } from 'react';
import {
  CodeBlock,
  DescriptionList,
  DescriptionListDetails,
  DescriptionListTerm,
  SegmentedControl,
  Tabs,
  TabsPanel,
} from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import type { Delivery, DeliveryDetail } from '@/services/db/delivery/entity';
import { isEmptyString } from '@/utils/string';
import { formatBodyJson } from '../utils/message-json';
import {
  orderedPushPlatforms,
  type Platform,
  type PushPreview,
  type PushPreviewFields,
  resolvePushPreview,
} from '../utils/push-payload';

const PUSH_TABS = [
  { value: 'message', text: 'Message' },
  { value: 'json', text: 'JSON' },
];

// iOS before Android (Ember push `platformOptions` order).
const PLATFORM_OPTIONS: { value: Platform; label: string; icon: IconName }[] = [
  { value: 'ios', label: 'iOS', icon: 'apple' },
  { value: 'android', label: 'Android', icon: 'android' },
];

type PushMessagePreviewProps = { delivery: Delivery; detail: DeliveryDetail };

/**
 * Push payload preview — Message (a Title / Body / Image / Link description list)
 * and JSON (the formatted body) tabs, ported from the Ember push `Previewer`.
 * A custom-payload push nests its content per platform in `detail.body`; when it
 * carries both an iOS and an Android body the Message tab shows a platform toggle
 * (Ember's device picker), initially selecting the delivery's first sent platform.
 * A single-platform or simple push resolves to one field set with no toggle.
 * Image and Link rows render only when present, matching the Ember `dl`.
 *
 * The device visual frame (the phone mockup) is composer-only — the delivery
 * detail renders the field list, matching Ember.
 */
export function PushMessagePreview({ delivery, detail }: PushMessagePreviewProps) {
  const preview = resolvePushPreview(detail);
  const { platformsWithBody } = preview;

  return (
    <Tabs tabs={PUSH_TABS} isBorderless data-test-delivery-preview-push-content>
      <TabsPanel value="message" p="200" data-test-push-message-tab>
        {platformsWithBody.length > 1 ? (
          <SwitchablePushMessage
            preview={preview}
            initialPlatform={orderedPushPlatforms(delivery, platformsWithBody)[0]}
          />
        ) : (
          <PushMessageFields fields={singlePlatformFields(preview)} />
        )}
      </TabsPanel>
      <TabsPanel value="json" p="200" data-test-push-json-tab>
        <CodeBlock className="fs-mask" code={formatBodyJson(detail.body)} language="json" withLineNumbers />
      </TabsPanel>
    </Tabs>
  );
}

/** The fields for the first platform carrying content, or the flat simple-push fields. */
function singlePlatformFields(preview: PushPreview): PushPreviewFields {
  const first = preview.platformsWithBody[0];
  return first != null ? (preview.byPlatform[first] ?? preview.fallback) : preview.fallback;
}

type SwitchablePushMessageProps = { preview: PushPreview; initialPlatform: Platform };

/** A custom payload with both platform bodies: a platform toggle above the resolved fields. */
function SwitchablePushMessage({ preview, initialPlatform }: SwitchablePushMessageProps) {
  const [platform, setPlatform] = useState<Platform>(initialPlatform);
  const fields = preview.byPlatform[platform] ?? preview.fallback;

  return (
    <>
      <SegmentedControl
        ariaLabel="Select platform"
        size="sm"
        options={PLATFORM_OPTIONS}
        value={platform}
        onChange={(value) => setPlatform(value as Platform)}
        mb="200"
        data-test-previewer-device-picker
      />
      <PushMessageFields fields={fields} />
    </>
  );
}

type PushMessageFieldsProps = { fields: PushPreviewFields };

function PushMessageFields({ fields }: PushMessageFieldsProps) {
  return (
    <DescriptionList layout="horizontal" className="fs-mask">
      <DescriptionListTerm text="product-p-secondary" color="subtle">
        Title
      </DescriptionListTerm>
      <DescriptionListDetails data-test-delivery-preview-push-title>{fields.title}</DescriptionListDetails>

      <DescriptionListTerm text="product-p-secondary" color="subtle">
        Body
      </DescriptionListTerm>
      <DescriptionListDetails data-test-delivery-preview-push-body style={{ whiteSpace: 'pre-wrap' }}>
        {fields.body}
      </DescriptionListDetails>

      {isEmptyString(fields.image) ? null : (
        <>
          <DescriptionListTerm text="product-p-secondary" color="subtle">
            Image
          </DescriptionListTerm>
          <DescriptionListDetails data-test-delivery-preview-push-image style={{ whiteSpace: 'pre-wrap' }}>
            {fields.image}
          </DescriptionListDetails>
        </>
      )}

      {isEmptyString(fields.link) ? null : (
        <>
          <DescriptionListTerm text="product-p-secondary" color="subtle">
            Link
          </DescriptionListTerm>
          <DescriptionListDetails data-test-delivery-preview-push-link style={{ whiteSpace: 'pre-wrap' }}>
            {fields.link}
          </DescriptionListDetails>
        </>
      )}
    </DescriptionList>
  );
}
